import type { DueGroup, DueList, TouchView } from "./api/OutreachTouchesApi";

export type StageKey = "send" | "waiting" | "message" | "follow_up" | "conversations" | "needs" | "today" | "closed";

export interface Stage {
  key: StageKey;
  title: string;
  /** Short name for the strip tile. */
  tab: string;
  hint: string;
  empty: string;
  groups: DueGroup[];
}

/** The pipeline a contact moves through, left to right. */
export const PIPELINE_STAGES: Stage[] = [
  { key: "send", tab: "Send request", title: "Connection requests to send", groups: ["requests_to_send"],
    hint: "Copy the note, send it on LinkedIn, then press Sent.", empty: "No connection requests to send." },
  { key: "waiting", tab: "Waiting for acceptance", title: "Requests waiting for acceptance", groups: ["requests_to_check"],
    hint: "Oldest first. Press Accepted when the person accepts. A request closes at the end of its day 7; Accepted still records a late one.",
    empty: "No requests are waiting." },
  { key: "message", tab: "Message to send", title: "Messages to send", groups: ["messages"],
    hint: "The person accepted: send the message at least 3 hours after the acceptance (the card shows the time).", empty: "No messages to send." },
  { key: "follow_up", tab: "Follow-up", title: "Follow-ups to send", groups: ["follow_ups"],
    hint: "Five days after the message.", empty: "No follow-ups to send." },
  { key: "conversations", tab: "Conversations", title: "Conversations", groups: [],
    hint: "People who replied. Their message first, oldest first: those are the ones waiting on us. A conversation stays here until you close it.",
    empty: "No open conversations." },
  { key: "closed", tab: "Stopped / skipped", title: "Stopped or skipped in the last 7 days", groups: ["stopped_recently"],
    hint: "Plans that ended without an answer, one card per company: why the LinkedIn path was dropped (no profile found, or closed by hand) and the drafts that were never sent. Companies that replied are in Conversations.", empty: "No plan was stopped or skipped in the last 7 days." },
];

/** Outside the pipeline: data to fix, and today's activity. */
export const SIDE_STAGES: Stage[] = [
  { key: "needs", tab: "Needs attention", title: "Needs attention", groups: ["needs", "waiting_for_start"],
    hint: "Nothing to copy yet: each card says what is missing. A campaign that has not started shows here until the Outreach Started milestone is recorded.",
    empty: "Nothing needs attention." },
  { key: "today", tab: "Recorded today", title: "Recorded today", groups: ["done_today"],
    hint: "You can still undo these today.", empty: "Nothing recorded today yet." },
];

export const ALL_STAGES: Stage[] = [...PIPELINE_STAGES, ...SIDE_STAGES];

/** The stage opened first when the URL names none: the first one that has something to do. */
// Waiting for acceptance is not a to-do (the next move is the other person's), so it only opens first when nothing else has work.
const DEFAULT_ORDER: StageKey[] = ["conversations", "send", "message", "follow_up", "needs", "waiting", "today", "closed"];

/** What a stage has to show: touches for the pipeline, the open conversations for Conversations, companies for Stopped / skipped. */
export function stageCount(stage: Stage, data: DueList): number {
  if (stage.key === "conversations") return (data.conversations ?? []).length;
  if (stage.key === "closed") return deadEnds(stageTouches(stage, data)).length;
  return stageTouches(stage, data).length;
}

/** One company's dropped LinkedIn path on one RFP: up to three touches that were stopped or skipped together. */
export interface StoppedGroup {
  key: string;
  opportunity_id: number;
  prime_company_id: number;
  /** `replied`: the path ended because they answered (good news); `skipped`: no profile or similar (a dead end); `other`. */
  cause: "replied" | "skipped" | "other";
  touches: TouchView[];
}

const KIND_ORDER: Record<string, number> = { connection_request: 0, acceptance_message: 1, follow_up: 2 };

/** Collapse the dropped touches to one entry per company and RFP, replies first, newest first within each cause. */
export function groupStopped(touches: TouchView[]): StoppedGroup[] {
  const byKey = new Map<string, StoppedGroup>();
  for (const t of touches) {
    const key = `${t.opportunity_id}:${t.prime_company_id}`;
    let g = byKey.get(key);
    if (!g) { g = { key, opportunity_id: t.opportunity_id, prime_company_id: t.prime_company_id, cause: "other", touches: [] }; byKey.set(key, g); }
    g.touches.push(t);
  }
  const rank = { replied: 0, skipped: 1, other: 2 } as const;
  const groups = [...byKey.values()];
  for (const g of groups) {
    g.touches.sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9));
    const reasons = g.touches.map((t) => (t.reason ?? "").toLowerCase());
    g.cause = reasons.some((r) => r.startsWith("replied")) ? "replied" : reasons.some((r) => r.startsWith("no profile found")) || g.touches.some((t) => t.state === "skipped") ? "skipped" : "other";
  }
  // sort is stable, so the server's order (newest first) is kept inside a cause
  return groups.sort((a, b) => rank[a.cause] - rank[b.cause]);
}

/** The dropped paths that are real dead ends. A company that replied is NOT listed: its reply is already in Conversations (the person
 *  recorded it), so a "stopped because they replied" card would only repeat it. */
export function deadEnds(touches: TouchView[]): StoppedGroup[] {
  return groupStopped(touches).filter((g) => g.cause !== "replied");
}

/** Conversations we wrote to that have gone quiet. */
export function noAnswer(data: DueList): number {
  return (data.conversations ?? []).filter((c) => c.no_answer).length;
}

/** Conversations whose last message is theirs: the only part of that stage that counts as work. */
export function waitingOnUs(data: DueList): number {
  return (data.conversations ?? []).filter((c) => c.state === "waiting_on_us").length;
}

const OVERDUE_FIRST: StageKey[] = ["send", "message", "follow_up"];

export function stageTouches(stage: Stage, data: DueList): TouchView[] {
  const rows = stage.groups.flatMap((g) => data.groups[g]);
  // Waiting: the request sent longest ago is the next to close, so it comes first.
  if (stage.key === "waiting") return [...rows].sort((a, b) => (a.sent_on ?? "9999").localeCompare(b.sent_on ?? "9999"));
  // Work due: the most overdue first (the sort is stable, so the server's order holds among equals).
  if (OVERDUE_FIRST.includes(stage.key)) return [...rows].sort((a, b) => b.overdue_days - a.overdue_days);
  return rows;
}

/** Cards of one RFP together, in the order the RFPs first appear (so the most urgent card still decides where its RFP sits). */
export interface RfpSection<T> { opportunity_id: number; title: string; items: T[] }
export function groupByRfp<T extends { opportunity_id: number; rfp_title: string | null }>(items: T[]): RfpSection<T>[] {
  const byId = new Map<number, RfpSection<T>>();
  for (const it of items) {
    let sec = byId.get(it.opportunity_id);
    if (!sec) { sec = { opportunity_id: it.opportunity_id, title: it.rfp_title ?? `RFP ${it.opportunity_id}`, items: [] }; byId.set(it.opportunity_id, sec); }
    sec.items.push(it);
  }
  return [...byId.values()];
}

/** What a search matches: the company, the person, their email and title, and the RFP. */
function searchText(r: { company_name: string | null; contact_name: string | null; contact_email: string | null; rfp_title: string | null; contact_title?: string | null }): string {
  return [r.company_name, r.contact_name, r.contact_email, r.contact_title, r.rfp_title].filter(Boolean).join(" ").toLowerCase();
}

/** The list narrowed to what matches every word typed (blank = everything). Counts and today's date are left as the server sent them. */
export function filterDueList(data: DueList, query: string): DueList {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return data;
  const hit = (r: Parameters<typeof searchText>[0]) => { const t = searchText(r); return words.every((w) => t.includes(w)); };
  const groups = Object.fromEntries(Object.entries(data.groups).map(([k, rows]) => [k, (rows as TouchView[]).filter(hit)])) as DueList["groups"];
  return { ...data, groups, coming_up: data.coming_up.filter(hit), conversations: (data.conversations ?? []).filter(hit) };
}

export function defaultStage(data: DueList): StageKey {
  const found = DEFAULT_ORDER.find((k) => {
    const stage = ALL_STAGES.find((s) => s.key === k)!;
    return k === "conversations" ? waitingOnUs(data) > 0 : stageTouches(stage, data).length > 0;
  });
  return found ?? "send";
}
